/* eslint-disable no-async-promise-executor */
/**
 * @module
 * @description Tokeny Vuex store, call APIs (with authentication token) and process their responses
 * @param {string} NODE_ENV process.env.NODE_ENV, deployment environment: production, testing, development
 * @param {string} API_URL_TOKEN_TRADER process.env.VUE_APP_API_URL_TOKEN_TRADER || REACT_APP_API_URL_TOKEN_TRADER, api endpoint for tokeny trader
 * @example
 * // In your @/store
 * import Vue from 'vue';
 * import Vuex from 'vuex';
 * import tokeny from '@tokenyico/vuejs-toolkit/src/exports/store/tokeny';
 * import locale from '@tokenyico/vuejs-toolkit/src/exports/store/locale';
 * import persist from '@tokenyico/vuejs-toolkit/src/exports/store/persist';
 * Vue.use(Vuex);
 * export default new Vuex.Store({
 *   modules: { locale, tokeny },
 *   plugins: [persist.plugin, tokeny.plugin]
 * });
 *
 * // In your VueJS component
 * import { mapActions, mapGetters } from 'vuex';
 * export default {
 *   computed: {
 *    ...mapGetters({
 *      email: 'tokeny/email',
 *      isLoggedIn: 'tokeny/isLoggedIn'
 *    })
 *  },
 *  methods: {
 *    ...mapActions({
 *      tokenyLogin: 'tokeny/login'
 *    }),
 *   }
 * };
 */
import Amplify, { Auth } from 'aws-amplify';
import Vue from 'vue';

const envProduction = process.env.NODE_ENV === 'production';
/* istanbul ignore next */
const tokenTraderApi = process.env.VUE_APP_API_URL_TOKEN_TRADER || process.env.REACT_APP_API_URL_TOKEN_TRADER;

/* istanbul ignore next */
Amplify.configure({
  Auth: {
    region: process.env.VUE_APP_AWS_REGION || process.env.REACT_APP_AWS_REGION,
    userPoolId: process.env.VUE_APP_AWS_USER_POOL_ID || process.env.REACT_APP_AWS_USER_POOL_ID,
    userPoolWebClientId: process.env.VUE_APP_AWS_USER_POOL_WEB_CLIENT_ID || process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
    mandatorySignIn: true
  }
});

/**
 * @typedef APIServiceError
 * @property {string} message
 * @property {number} statusCode
 * @property {?Object<string, *>} data
 * @property {?string} stack
 */
export class ApiServiceError extends Error {
  constructor (error) {
    /* istanbul ignore next */
    if (!envProduction) console.error(JSON.stringify(error));
    super(error.message);
    for (const key of Object.keys(error)) {
      this[key] = error[key];
    }
  }
};

const emptyState = () => ({
  /**
   * @name state
   * @type {object}
   * @property {object} cognito AWS Cognito data storage
   * @property {string} cognito.jwt JWT to call TokenTrader
   * @property {number} cognito.exp JWT expiry date
   * @property {number} cognito.timeout JWT refresh timeout
   * @property {object} tokenTrader TokenTrader data storage
   * @property {string} tokenTrader.jwt JWT to call Tokeny APIs
   * @property {object} tokenTrader.jwtData JWT deserialised data content
   * @property {object} tokenTrader.account Account object
   * @property {object} tokenTrader.timeout JWT refresh timeout
   */
  cognito: {
    jwt: null,
    exp: null,
    timeout: null
  },
  tokenTrader: {
    jwt: null,
    jwtData: null,
    account: null,
    timeout: null
  }
});

export default {
  namespaced: true,
  state: emptyState,
  /**
   * @name getters
   * @type {object}
   * @getter {boolean} isLoggedIn=tokenTrader.jwt
   * @getter {string} email=tokenTrader.jwtData.email
   * @getter {string} jwt=tokenTrader.jwtData.jwt
   * @getter {object} tokenTraderAccount=tokenTrader.account
   */
  getters: {
    isLoggedIn: (state) => state.tokenTrader.jwt !== null,
    email: (state) => state.tokenTrader && state.tokenTrader.jwtData && state.tokenTrader.jwtData.email,
    jwt: (state) => state.tokenTrader && state.tokenTrader.jwt,
    tokenTraderAccount: (state) => state.tokenTrader.account
  },
  actions: {
    /**
     * Initiate login
     * @action login=isLoggedIn
     * @async
     * @param {object} payload
     * @param {string} payload.username
     * @param {string} payload.password
     * @returns {void}
     * @example
     * // Within your VueJS component
     * ...
     *   methods: {
     *   ...mapActions({
     *     tokenyLogin: 'tokeny/login'
     *   }),
     *   async signIn () {
     *     this.submitting = true;
     *     try {
     *       await this.tokenyLogin({
     *         username: this.email,
     *         password: this.password
     *       });
     *       // Toast and forward to app handled by mixins/guards/authGuard
     *     } catch (error) {
     *       if (
     *         error.code === 'UserNotFoundException' ||
     *         error.code === 'NotAuthorizedException'
     *       ) {
     *         this.$logger.info(this.$t('view.SignIn.failureMessage'));
     *       } else {
     *         this.$logger.error(error);
     *       }
     *     }
     *     this.submitting = false;
     *   }
     * ...
     */
    async login ({ commit, dispatch, state }, { username, password }) {
      const user = await Auth.signIn(username, password);
      const session = await Auth.currentSession();
      commit('cognito', {
        jwt: session.getIdToken().getJwtToken(),
        exp: session.getAccessToken().getExpiration()
      });
      await dispatch('fetchTokenTraderAccount');
      if (!state.tokenTrader.account) {
        await dispatch('createTokenTraderAccount');
      }
      await dispatch('fetchTokenTraderToken');

      const cognitoExp = state.cognito.exp * 1000 - Date.now() - 5000;
      commit('cognitoTimeout', setTimeout(
        () => dispatch('refreshCognitoToken'),
        cognitoExp)
      );

      return user;
    },

    /**
     * Initiate logout
     * @action logout=isLoggedIn
     * @async
     * @returns {void}
     * @example
     * // Within your VueJS component
     * ...
     * actions: {
     *   ...mapActions({
     *     logout: 'tokeny/logout' // Toast and forward to sign in is handled by mixins/guards/appGuard
     *   }),
     * }
     * ...
     */
    async logout ({ state, commit }) {
      await Auth.signOut();
      commit('reset');

      clearTimeout(state.cognito.timeout);
      commit('cognitoTimeout', null);

      clearTimeout(state.tokenTrader.timeout);
      commit('tokenTraderTimeout', null);
    },

    /**
     * Initiate forgotten password
     * @action forgotPassword=null
     * @async
     * @param {object} payload
     * @param {string} payload.username
     * @param {?json} payload.clientMetadata
     * @returns {void}
     */
    async forgotPassword (_context, { username, clientMetadata }) {
      if (clientMetadata) return Auth.forgotPassword(username, clientMetadata);
      else return Auth.forgotPassword(username);
    },

    /**
     * Confirm forgotten password
     * @action forgotPasswordConfirm=null
     * @async
     * @param {object} payload
     * @param {string} payload.username
     * @param {string} payload.code
     * @param {string} payload.password
     * @returns {void}
     */
    async forgotPasswordConfirm (_context, { username, code, password }) {
      return Auth.forgotPasswordSubmit(username, code, password);
    },

    /**
     * Call a Tokeny API with TokenTrader JWT
     * @action callAPI=null
     * @async
     * @param {object} payload
     * @param {string} payload.api
     * @param {string} payload.path
     * @param {'GET'|'POST'|'PUT'|'DELETE'} payload.method
     * @param {?Object.<string,string>} payload.headers
     * @param {?Object.<string,*>} payload.body
     * @param {?Object.<string,*>} payload.queryParams
     * @returns {Object.<string,*>}
     * @throws {APIServiceError}
     * @example
     * // Within your @/store/modules/X
     * const api = 'http://api-endpoint/api/';
     * ...
     * actions: {
     *   async fetch ({ dispatch }, { path, method = 'GET', headers = {}, body = null, queryParams = {} }) {
     *     return dispatch('tokeny/callAPI', {
     *       api, path, method, headers, body, queryParams
     *     }, { root: true });
     *   },
     *   async myAction ({ dispatch }, { some, payload }) {
     *     const apiDataResponse = await dispatch('fetch', { path: 'my/path/', body: { some, payload } });
     *   }
     * },
     * ...
     */
    async callAPI ({ dispatch, state }, { api, path, method, headers = {}, body, queryParams, mode, redirect, referrerPolicy }) {
      return dispatch('fetch', {
        api,
        path,
        method,
        body,
        queryParams,
        headers: {
          ...headers,
          Authorization: `Bearer ${state.tokenTrader.jwt}`
        },
        mode,
        redirect,
        referrerPolicy
      });
    },

    /**
     * Internal function refresh cognito jwt
     * @action refreshCognitoToken=cognito.jwt
     * @async
     * @returns {void}
     */
    async refreshCognitoToken ({ state, commit, dispatch }) {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        await new Promise((resolve, reject) => {
          cognitoUser.refreshSession(session.getRefreshToken(), (err, session) => {
            if (err) return reject(err);

            commit('cognito', {
              jwt: session.getIdToken().getJwtToken(),
              exp: session.getAccessToken().getExpiration()
            });
            resolve();
          });
        });

        const cognitoExp = state.cognito.exp * 1000 - Date.now() - 5000;
        clearTimeout(state.cognito.timeout);
        commit('cognitoTimeout', setTimeout(
          () => dispatch('refreshCognitoToken'),
          cognitoExp)
        );
      } catch (e) {
        dispatch('logout');
        throw e;
      }
    },

    /**
     * Internal function to call TokenTrader with Cognito JWT
     * @action callTokenTrader=null
     * @async
     * @param {object} payload
     * @param {string} payload.api
     * @param {string} payload.path
     * @param {'GET'|'POST'|'PUT'|'DELETE'} payload.method
     * @param {?Object.<string,string>} payload.headers
     * @param {?Object.<string,*>} payload.body
     * @param {?Object.<string,*>} payload.queryParams
     * @returns {Object.<string,*>}
     * @throws {APIServiceError}
     */
    async callTokenTrader ({ dispatch, state }, { path, method = 'GET', headers = {}, body = null, queryParams = {} }) {
      return dispatch('fetch', {
        path,
        method,
        body,
        queryParams,
        api: tokenTraderApi,
        headers: {
          ...headers,
          Authorization: `Bearer ${state.cognito.jwt}`
        }
      });
    },

    /**
     * Internal function fetch JWT from TokenTrader
     * @action fetchTokenTraderToken=tokenTrader.jwt
     * @async
     * @returns {void}
     */
    async fetchTokenTraderToken ({ state, dispatch, commit }) {
      let jwtData = null;
      const { token } = await dispatch('callTokenTrader', {
        method: 'POST',
        path: '/token'
      });
      if (token) {
        jwtData = JSON.parse(atob(token.split('.')[1]));
        const tokenTraderMs = jwtData.exp * 1000 - Date.now() - 5000;
        clearTimeout(state.tokenTrader.timeout);
        commit('tokenTraderTimeout', setTimeout(
          () => dispatch('fetchTokenTraderToken'),
          tokenTraderMs
        ));
      }

      commit('tokenTraderJwt', { jwt: token, jwtData });
    },

    /**
     * Internal function to fetch Account from TokenTrader using jwt token trader from external provider
     * @action fetchTokenTraderAccount=tokenTrader.account
     * @async
     * @returns {void}
     */
    async SSOLogin ({ dispatch, commit, state }, { jwt }) {
      let jwtData = null;
      // set cognito jwt as token trader jwt
      commit('cognito', { jwt, exp: 1e12 });
      // set token trader jwt

      if (jwt) {
        jwtData = JSON.parse(atob(jwt.split('.')[1]));
      }
      commit('tokenTraderJwt', { jwt, jwtData });

      await dispatch('fetchTokenTraderAccount');

      if (!state.tokenTrader.account) {
        throw new Error('TokenTraderAccountDoesntExist');
      }
    },

    /**
     * Internal function to fetch Account from TokenTrader
     * @action fetchTokenTraderAccount=tokenTrader.account
     * @async
     * @returns {void}
     */
    async fetchTokenTraderAccount ({ dispatch, commit }) {
      try {
        const res = await dispatch('callTokenTrader', {
          path: '/shared-accounts/me'
        });
        const account = res.accounts && res.accounts[0] ? res.accounts[0] : false;
        commit('tokenTraderAccount', account);
      } catch (e) {
        if (e.statusCode === 404) commit('tokenTraderAccount', false);
        else throw e;
      }
    },

    /**
     * Internal function to create an Account on TokenTrader
     * @action createTokenTraderAccount=tokenTrader.account
     * @async
     * @returns {void}
     */
    async createTokenTraderAccount ({ dispatch }) {
      await dispatch('callTokenTrader', {
        path: '/accounts',
        method: 'POST'
      });
      await dispatch('fetchTokenTraderAccount');
    },

    /**
     * Internal fetch wrapper, call an API, handle error responses
     * @action fetch=null
     * @async
     * @param {object} payload
     * @param {string} payload.api
     * @param {string} payload.path
     * @param {'GET'|'POST'|'PUT'|'DELETE'} payload.method
     * @param {?Object.<string,string>} payload.headers
     * @param {?Object.<string,*>} payload.body
     * @param {?Object.<string,*>} payload.queryParams
     * @returns {Object.<string,*>}
     * @throws {APIServiceError}
     */
    fetch: (_store, { api, path, method, headers = {}, body = null, queryParams = {}, mode, redirect, referrerPolicy }) => new Promise(async (resolve, reject) => {
      const url = new URL(api + path);
      url.search = new URLSearchParams(queryParams);

      const response = await fetch(url.toString(), {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: body ? JSON.stringify(body) : undefined,
        mode,
        redirect,
        referrerPolicy
      });

      const resCopy = response.clone();
      if (!response.ok) {
        let error;
        try {
          error = await resCopy.json();
        } catch (e) {
          error = { message: 'JSON response expected', body: await response.text() };
        }
        error.statusCode = response.status;
        reject(new ApiServiceError(error));
      } else {
        let ret;
        try {
          ret = await resCopy.json();
        } catch (e) {
          const body = await response.text();
          if (body === '') {
            console.log('Response contained empty body: ' + url.toString());
            resolve();
          } else {
            reject(new ApiServiceError({ message: 'JSON response expected', body }));
          }
        }
        resolve(ret);
      }
    })
  },
  mutations: {
    reset (state) {
      const newState = emptyState();
      for (const key of Object.keys(newState)) {
        Vue.set(state, key, newState[key]);
      }
    },
    cognito (state, { jwt, exp }) {
      state.cognito.jwt = jwt;
      state.cognito.exp = exp;
    },
    cognitoTimeout (state, timeout) {
      state.cognito.timeout = timeout;
    },
    tokenTraderJwt (state, { jwt, jwtData }) {
      state.tokenTrader.jwt = jwt;
      state.tokenTrader.jwtData = jwtData;
    },
    tokenTraderAccount (state, account) {
      state.tokenTrader.account = account;
    },
    tokenTraderTimeout (state, timeout) {
      state.tokenTrader.timeout = timeout;
    }
  },
  plugin () {

  }
};
